/**
 * Browser information utilities - detailed device and browser data collection
 */

/**
 * Get current page URL
 */
export function getCurrentUrl(): string {
  return window.location.href;
}

/**
 * Get referrer URL
 */
export function getReferrer(): string | undefined {
  return document.referrer || undefined;
}

/**
 * Get user agent string
 */
export function getUserAgent(): string | undefined {
  return navigator.userAgent || undefined;
}

/**
 * Parse user agent to extract browser info
 */
export function getBrowserInfo() {
  const ua = navigator.userAgent;
  const browsers = [
    { name: 'Edge', regex: /Edg\/(\d+)/ },
    { name: 'Chrome', regex: /Chrome\/(\d+)/ },
    { name: 'Firefox', regex: /Firefox\/(\d+)/ },
    { name: 'Safari', regex: /Version\/(\d+).*Safari/ },
    { name: 'Opera', regex: /OPR\/(\d+)/ },
    { name: 'IE', regex: /MSIE (\d+)/ },
  ];

  for (const browser of browsers) {
    const match = ua.match(browser.regex);
    if (match) {
      return {
        name: browser.name,
        version: match[1],
        fullVersion: match[0],
      };
    }
  }

  return {
    name: 'Unknown',
    version: '',
    fullVersion: ua,
  };
}

/**
 * Get operating system information
 */
export function getOSInfo() {
  const ua = navigator.userAgent;
  const platform = navigator.platform;

  // Mobile detection
  if (/Android/i.test(ua)) {
    const match = ua.match(/Android\s([\d.]+)/);
    return { name: 'Android', version: match ? match[1] : '', mobile: true };
  }
  if (/iPhone|iPad|iPod/i.test(ua)) {
    const match = ua.match(/OS\s([\d_]+)/);
    return {
      name: 'iOS',
      version: match ? match[1].replace(/_/g, '.') : '',
      mobile: true,
      device: /iPhone/i.test(ua) ? 'iPhone' : /iPad/i.test(ua) ? 'iPad' : 'iPod',
    };
  }

  // Desktop detection
  if (/Windows NT/i.test(ua)) {
    const match = ua.match(/Windows NT\s([\d.]+)/);
    const versionMap: Record<string, string> = {
      '10.0': '10/11',
      '6.3': '8.1',
      '6.2': '8',
      '6.1': '7',
    };
    const version = match ? match[1] : '';
    return { name: 'Windows', version: versionMap[version] || version, mobile: false };
  }
  if (/Mac OS X/i.test(ua)) {
    const match = ua.match(/Mac OS X\s([\d_]+)/);
    return {
      name: 'macOS',
      version: match ? match[1].replace(/_/g, '.') : '',
      mobile: false,
    };
  }
  if (/Linux/i.test(ua)) {
    return { name: 'Linux', version: '', mobile: false };
  }

  return { name: platform || 'Unknown', version: '', mobile: false };
}

/**
 * Get device type
 */
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const ua = navigator.userAgent;

  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Get network information
 */
export function getNetworkInfo() {
  const connection = (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;

  if (!connection) {
    return null;
  }

  return {
    effectiveType: connection.effectiveType, // '4g', '3g', '2g', 'slow-2g'
    downlink: connection.downlink, // Mbps
    rtt: connection.rtt, // ms
    saveData: connection.saveData, // boolean
  };
}

/**
 * Get battery status
 */
export async function getBatteryInfo() {
  try {
    if ('getBattery' in navigator) {
      const battery = await (navigator as any).getBattery();
      return {
        charging: battery.charging,
        level: Math.round(battery.level * 100),
        chargingTime: battery.chargingTime,
        dischargingTime: battery.dischargingTime,
      };
    }
  } catch {
    // Battery API not available
  }
  return null;
}

/**
 * Check if user is using ad blocker
 */
export function hasAdBlocker(): boolean {
  const adTest = document.createElement('div');
  adTest.innerHTML = '&nbsp;';
  adTest.className = 'adsbox';
  adTest.style.position = 'absolute';
  adTest.style.left = '-9999px';
  document.body.appendChild(adTest);

  const isBlocked = adTest.offsetHeight === 0;
  document.body.removeChild(adTest);

  return isBlocked;
}

/**
 * Get locale and language preferences
 */
export function getLocaleInfo() {
  return {
    language: navigator.language,
    languages: navigator.languages || [navigator.language],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
  };
}

/**
 * Get device memory (if available)
 */
export function getDeviceMemory(): number | null {
  return (navigator as any).deviceMemory || null;
}

/**
 * Get hardware concurrency (CPU cores)
 */
export function getHardwareConcurrency(): number | null {
  return navigator.hardwareConcurrency || null;
}

/**
 * Get max touch points
 */
export function getMaxTouchPoints(): number {
  return navigator.maxTouchPoints || 0;
}

/**
 * Check if cookies are enabled
 */
export function areCookiesEnabled(): boolean {
  return navigator.cookieEnabled;
}

/**
 * Get Do Not Track setting
 */
export function getDoNotTrack(): string | null {
  return navigator.doNotTrack || (window as any).doNotTrack || (navigator as any).msDoNotTrack || null;
}

/**
 * Get comprehensive device context
 */
export async function getDeviceContext() {
  return {
    browser: getBrowserInfo(),
    os: getOSInfo(),
    deviceType: getDeviceType(),
    network: getNetworkInfo(),
    battery: await getBatteryInfo(),
    locale: getLocaleInfo(),
    hardware: {
      memory: getDeviceMemory(),
      cpuCores: getHardwareConcurrency(),
      touchPoints: getMaxTouchPoints(),
    },
    privacy: {
      cookiesEnabled: areCookiesEnabled(),
      doNotTrack: getDoNotTrack(),
      adBlocker: hasAdBlocker(),
    },
  };
}
